import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import Configs from 'config';
import { get } from 'lodash';
import { UserCreditExchangeWrapper } from 'components/user/UserCreditExchange';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { getCreditExchangeInfo, creditExchangeCreate } from 'libs/api/user';
import { useDataApi } from 'libs/hooks';
import { filteredFileNames } from 'libs/utils';
import { commifyFormatter } from 'libs/validators';
import { TCreditExchangeCreate, TCreditExchangeInfo } from 'modules/user';
import { CUploadFileProps } from 'constants/base.const';
import { ENotificationType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { message, Upload, Spin, Button, Form, Tabs, Col, Row, Typography, Checkbox } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FormWrapper } from 'GlobalStyles';

const { TabPane } = Tabs;
const { Text, Title } = Typography;

const UserCreditExchange = () => {
  const [form] = Form.useForm();
  const [{ data: exchangeData, loading: exchangeLoading }] = useDataApi<TCreditExchangeInfo>(
    getCreditExchangeInfo.bind(null),
  );
  const history = useHistory();
  const [uploadAccountVisible, setUploadAccountVisible] = useState(true);
  const [uploadIdCardVisible, setUploadIdCardVisible] = useState(true);
  const [uploadRegistrationVisible, setUploadRegistrationVisible] = useState(true);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  function onClickSubmit() {
    form
      .validateFields()
      .then((value) => {
        handleSubmit(value as TCreditExchangeCreate);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  }

  async function handleSubmit(value: TCreditExchangeCreate) {
    try {
      setSubmitButtonDisabled(true);
      const response = await creditExchangeCreate({
        bank_account_file_ids: filteredFileNames(get(value, 'bank_account_files')),
        id_card_file_ids: filteredFileNames(get(value, 'id_card_files')),
        registration_file_ids: filteredFileNames(get(value, 'registration_files')),
        reuse_file: get(value, 'reuse_file', false),
      } as TCreditExchangeCreate);

      history.push({
        pathname: ERoute.UserCreditExchange,
        state: {
          notification: {
            type: ENotificationType.Success,
            message: '정산신청을 완료했습니다.',
          },
        },
      });
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  }

  useEffect(() => {
    if (get(exchangeData, 'bank_account_file')) {
      form.setFieldsValue({
        bank_account_files: [
          {
            uid: get(exchangeData, 'bank_account_file.id'),
            status: 'done',
            name: get(exchangeData, 'bank_account_file.file_name'),
            absolute_url: get(exchangeData, 'bank_account_file.absolute_url'),
          },
        ],
      });
      setUploadAccountVisible(false);
    }
    if (get(exchangeData, 'id_card_file')) {
      form.setFieldsValue({
        id_card_files: [
          {
            uid: get(exchangeData, 'id_card_file.id'),
            status: 'done',
            name: get(exchangeData, 'id_card_file.file_name'),
            absolute_url: get(exchangeData, 'id_card_file.absolute_url'),
          },
        ],
      });
      setUploadIdCardVisible(false);
    }
    if (get(exchangeData, 'registration_file')) {
      form.setFieldsValue({
        registration_files: [
          {
            uid: get(exchangeData, 'registration_file.id'),
            status: 'done',
            name: get(exchangeData, 'registration_file.file_name'),
            absolute_url: get(exchangeData, 'registration_file.absolute_url'),
          },
        ],
      });
      setUploadRegistrationVisible(false);
    }
  }, [exchangeData]);

  return (
    <UserCreditExchangeWrapper>
      <div
        style={{
          maxWidth: '600px',
          margin: 'auto',
          padding: '40px 10px 0px 10px',
        }}
      >
        <Title level={2}>정산신청</Title>
        <div className='content-box' style={{ backgroundColor: '#2a4753', marginBottom: '20px' }}>
          <Spin tip='loading...' spinning={exchangeLoading} delay={1000}>
            <Row justify='space-between' style={{ maxWidth: '500px', margin: 'auto' }} align='middle'>
              <Col xs={24} sm={24} md={10} lg={10} xl={10}>
                <div className='large-text'>
                  {commifyFormatter(String(get(exchangeData, 'exchangeable_amount')))}원
                </div>
                <div className='small-text'>정산 예상 금액</div>
              </Col>
              <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                <div className='large-text'>
                  {commifyFormatter(String(get(exchangeData, 'exchangeable_count')))}회
                </div>
                <div className='small-text'>수업 횟수</div>
              </Col>
            </Row>
          </Spin>
        </div>
        <div style={{ color: '#000' }}>
          <InfoCircleOutlined /> 국세 및 지방세 3.3% 공제 후 지급됩니다.
        </div>
        <div style={{ color: '#000' }}>
          <InfoCircleOutlined /> 정산 받으신 다음 해 5월 종합소득 신고 시, 정산 금액을 고지하셔야 합니다.
        </div>
        <div style={{ color: '#000' }}>
          <InfoCircleOutlined /> 업로드한 파일 및 개인정보는 정산용으로만 사용되며, 이외의 어떤 목적으로도
          사용 또는 가공되지 않습니다.
        </div>
        <FormWrapper style={{ paddingTop: '35px' }}>
          <Form form={form} layout='vertical' className='form-normal' initialValues={{ reuse_file: true }}>
            <div className='content-title' style={{ marginBottom: '15px' }}>
              통장 사본 업로드
            </div>
            <Form.Item
              name={'bank_account_files'}
              className='form-item'
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.bank_account_files !== currentValues.bank_account_files
              }
              valuePropName='fileList'
              label='본인 명의 통장'
              getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            >
              <Upload
                name='file'
                {...CUploadFileProps}
                action={`${Configs.API_HOST}/credit/exchange/file`}
                onChange={(info: UploadChangeParam) => {
                  const { status } = info.file;

                  if (status === 'done') {
                    message.success(`${info.file.name} 업로드에 성공했습니다.`);
                    setUploadAccountVisible(false);
                  } else if (status === 'error') {
                    message.error(`${info.file.name} 업로드에 실패했습니다.`);
                  } else if (status === 'removed') {
                    if (info.fileList.length === 0) {
                      setUploadAccountVisible(true);
                    }
                  }
                }}
              >
                {uploadAccountVisible === true && (
                  <>
                    <span className='file-placeholder'>정산 받을 통장 사본 업로드 해주세요.</span>
                    <div style={{ float: 'right' }} className='input-suffix'>
                      업로드
                    </div>
                  </>
                )}
              </Upload>
            </Form.Item>
            <div className='content-title' style={{ marginBottom: '15px', marginTop: '30px' }}>
              세금 처리를 위해 신분증 혹은 사업자등록증 사본 업로드
            </div>
            <Form.Item
              name={'id_card_files'}
              className='form-item'
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.id_card_files !== currentValues.id_card_files
              }
              valuePropName='fileList'
              label='신분증'
              getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            >
              <Upload
                name='file'
                {...CUploadFileProps}
                action={`${Configs.API_HOST}/credit/exchange/file`}
                onChange={(info: UploadChangeParam) => {
                  const { status } = info.file;

                  if (status === 'done') {
                    message.success(`${info.file.name} 업로드에 성공했습니다.`);
                    setUploadIdCardVisible(false);
                  } else if (status === 'error') {
                    message.error(`${info.file.name} 업로드에 실패했습니다.`);
                  } else if (status === 'removed') {
                    if (info.fileList.length === 0) {
                      setUploadIdCardVisible(true);
                    }
                  }
                }}
              >
                {uploadIdCardVisible === true && (
                  <>
                    <span className='file-placeholder'>
                      주민등록증, 운전면허증 혹은 여권을 업로드 해주세요
                    </span>
                    <div style={{ float: 'right' }} className='input-suffix'>
                      업로드
                    </div>
                  </>
                )}
              </Upload>
            </Form.Item>
            <Form.Item
              name={'registration_files'}
              className='form-item'
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.registration_files !== currentValues.registration_files
              }
              valuePropName='fileList'
              label='사업자 등록증'
              getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            >
              <Upload
                name='file'
                {...CUploadFileProps}
                action={`${Configs.API_HOST}/credit/exchange/file`}
                onChange={(info: UploadChangeParam) => {
                  const { status } = info.file;

                  if (status === 'done') {
                    message.success(`${info.file.name} 업로드에 성공했습니다.`);
                    setUploadRegistrationVisible(false);
                  } else if (status === 'error') {
                    message.error(`${info.file.name} 업로드에 실패했습니다.`);
                  } else if (status === 'removed') {
                    if (info.fileList.length === 0) {
                      setUploadRegistrationVisible(true);
                    }
                  }
                }}
              >
                {uploadRegistrationVisible === true && (
                  <>
                    <span className='file-placeholder'>사업자 등록증을 업로드 해주세요</span>
                    <div style={{ float: 'right' }} className='input-suffix'>
                      업로드
                    </div>
                  </>
                )}
              </Upload>
            </Form.Item>
            <Form.Item name={'reuse_file'} valuePropName={'checked'} style={{ marginBottom: '15px' }}>
              <Checkbox style={{ fontSize: '13px', width: '100%' }}>
                차후 정산에도 첨부 파일 활용에 동의합니다.
              </Checkbox>
            </Form.Item>
            <Form.Item shouldUpdate={true} noStyle={true}>
              {() => (
                <div className='steps-action'>
                  <Button
                    type='primary'
                    htmlType='submit'
                    size='large'
                    loading={submitButtonDisabled}
                    onClick={onClickSubmit}
                  >
                    정산신청
                  </Button>
                </div>
              )}
            </Form.Item>
          </Form>
        </FormWrapper>
      </div>
    </UserCreditExchangeWrapper>
  );
};

export default UserCreditExchange;
